'use client';

import ModalCartPage from '@/app/(webapp)/_components/add-cart-modal';
import { AutoResizeInput } from '@/app/(webapp)/_components/auto-resize-input';
import { ConfirmPublicationModal } from '@/app/(webapp)/_components/confirm-publication-modal';
import { ExcludedCountriesModals } from '@/app/(webapp)/_components/modals/excluded-countries-modal';
import OtherLanguageModal from '@/app/(webapp)/_components/modals/other-language-modal';
import VideoModal from '@/app/(webapp)/_components/video-modal';
import { localeMap } from '@/app/data/localMap';
import CoverImage from '@/components/cover-image';
import AppButton from '@/components/ui/app-button';
import { Button } from '@/components/ui/button';
import { IconPublish, IconUnpublish, IconValidate } from '@/components/ui/icons';
import Modal from '@/components/ui/modal';
import { useAuth } from '@/context/auth';
import { useCart } from '@/context/auth/cart-context';
import { useUserData } from '@/context/auth/user-data-context';
import {
    type Buyer,
    Category,
    CategoryProgram,
    ContentType,
    CountryIsoCode,
    ExclusionType,
    Genre,
    GenreProgram,
    LanguageIsoCode,
    type Program,
} from '@/graphql/graphql';
import { DetailsObject } from '@/graphql/scalars';
import { useRouter } from '@/i18n/navigation';
import { fetchCategoriesList } from '@/lib/api-categories';
import { fetchGenresList } from '@/lib/api-genres';
import { getEpisodesPrice, getProgram, updateProgram } from '@/lib/api-programs';
import { formatDuration, getFallbackTitle, isoEn } from '@/lib/utils';
import iconPlayWhite from '@public/icons/icon-play-bg-white.svg';
import iconPlayBorderWhite from '@public/icons/icon-play-border-white.svg';
import iconInfoWhite from '@public/icons/info.svg';
import { formatISO } from 'date-fns';
import { useLocale, useMessages, useTranslations } from 'next-intl';
import Image from 'next/image';
import { notFound, useParams } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';
import { EpisodesList } from './_components/episodes-list';
import Metadata from './_components/metadata';
import { TechnicalInfoModal } from './_components/technical-info-modal';
import { useEpisodePagination } from './_hooks/use-episode-pagination';
import { ProgramDetailsProps } from './interfaces';

function ProgramLoadingPage() {
    return (
        <div className="py-[50px] w-[89%] mx-auto flex flex-col lg:flex-row lg:items-start">
            <div className="w-full">
                <div className="inline-block w-full">
                    <div className="inline-block w-full lg:w-4/6 lg:pr-15">
                        <div className="flex flex-col items-start md:flex-row gap-7 lg:gap-0">
                            <div className="max-w-[100vw] md:max-w-[241px] w-full">
                                <div className="aspect-3/4 relative animate-pulse bg-white/10 rounded-[6px]" />
                            </div>
                            <div className="md:ml-15 flex-1 flex flex-col">
                                <div className="h-[36px] mb-1 animate-pulse bg-white/10 rounded-[6px]" />
                                <div className="h-[27px] mb-[30px] animate-pulse bg-white/10 rounded-[6px]" />
                                <div className="h-[100px] animate-pulse bg-white/10 rounded-[6px]" />
                            </div>
                        </div>
                    </div>
                    <div className="w-full lg:w-2/6 inline-block lg:float-right my-8 lg:my-0">
                        <div className="h-[660px] w-full animate-pulse bg-white/10 rounded-xl" />
                    </div>
                </div>
            </div>
        </div>
    );
}

// @TODO: Clean this part
const formateNumber = (input: string | number) => {
    const num = Number.parseFloat((input || '').toString());
    if (Number.isNaN(num)) return '0';
    let integerPart = Math.trunc(num).toString();
    integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return integerPart;
};

type RestrictionType = '' | 'classic' | 'FAST' | 'local_vod';

const formInputClassName: string = `
    cursor-pointer
    items-center flex justify-between
    text-sm text-white p-[8px]
    bg-[rgba(255,255,255,0.1)] rounded-[8px]
    placeholder-white
    border border-1 border-white
    hover:bg-transparent
    focus:outline-none focus:ring-0 focus-visible:ring-0
    transition-all duration-300`;

const editViewEpisodeClassName: string = 'group-hover:bg-white/10 transition-colors duration-300';

export default function ProgramDetails() {
    const { id } = useParams() as { id: string };
    const [program, setProgram] = useState<Program | null>(null);
    const [loading, setLoading] = useState(true);
    const [isPublishProgramModalOpen, setIsPublishProgramModalOpen] = useState(false);
    const [showVideoModal, setShowVideoModal] = useState(false);
    const [showOtherLanguage, setShowOtherLanguage] = useState(false);
    const { user } = useAuth();
    const [isInfoOpen, setIsInfoOpen] = useState(false);
    const [programDetails, setProgramDetails] = useState<DetailsObject | null | undefined>(undefined);
    const [isExcludedCountriesModalOpen, setIsExcludedCountriesModalOpen] = useState(false);
    const [categories, setCategories] = useState<Category[]>([]);
    const [genres, setGenres] = useState<Genre[]>([]);

    const [selectedGenres, setSelectedGenres] = useState<string[]>([]);

    const [deselectedEpisodesIds, setDeselectedEpisodesIds] = useState<string[]>([]);
    const [calculatedPrice, setCalculatedPrice] = useState<number | null>(null);

    const [isTechnicalInfoCardActive, setIsTechnicalInfoCardActive] = useState<boolean>(false);
    const [showModalCart, setShowModalCart] = useState(false);
    const [isNotRegistered, setIsNotRegistered] = useState(false);
    const [selectedEpisode, setSelectedEpisode] = useState<Program | null>(null);
    const isoLocale = useLocale().toLowerCase() as LanguageIsoCode;
    const t = useTranslations('ScreeningRoom');
    const tp = useTranslations('ProgramScreeningRoom');
    const tl = useTranslations('IsoLanguage');
    const tc = useTranslations('IsoCountry');
    const messages = useMessages();

    const toCamelCase = (value: string) =>
        value
            .trim()
            .replace(/[^a-zA-Z0-9]+(.)/g, (_, character: string) => character.toUpperCase())
            .replace(/^[A-Z]/, (character) => character.toLowerCase());

    const getTranslatedCartErrorMessage = (message: string) => {
        const translatedErrors = messages.AddCartModal.errors as Record<string, string>;

        return translatedErrors[toCamelCase(message)] ?? message;
    };

    const countries = useMemo(
        () =>
            Object.values(CountryIsoCode)
                .map((code) => ({
                    value: code,
                    label: tc(code),
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [tc],
    );
    const { isSeller, isAdmin, isBuyer, isReadOnly, user: userData, isAnonymous, loading: loadingUser } = useUserData();

    const hasEpisodes = (program?.episodesQuantity ?? 0) > 0;

    const { episodes, totalCount, loadingInitial, loadingMore, hasMore, loadMoreEpisodes, setEpisodes } =
        useEpisodePagination({
            programId: id,
            includeMasterFiles: isAdmin || isSeller || isBuyer,
            enabled: hasEpisodes,
        });

    useEffect(() => {
        if (episodes.length > 0) {
            return;
        }
        const isActive = checkDetailsAvailability();
        setIsTechnicalInfoCardActive(isActive);
    }, [episodes]);

    useEffect(() => {
        if (!isBuyer || episodes.length === 0 || isSeller || isAdmin) {
            setCalculatedPrice(null);
            return;
        }

        if (deselectedEpisodesIds.length === 0) {
            setCalculatedPrice(program?.price ?? 0);
            return;
        }

        if (deselectedEpisodesIds.length === totalCount) {
            setCalculatedPrice(0);
            return;
        }

        let cancelled = false;

        const timeoutId = setTimeout(async () => {
            const selectedIds = episodes
                .map((ep) => ep.identifier.id)
                .filter((id) => !deselectedEpisodesIds.includes(id));

            if (selectedIds.length === 0) {
                setCalculatedPrice(0);
                return;
            }

            const price = await getEpisodesPrice(id, deselectedEpisodesIds);
            if (!cancelled) {
                setCalculatedPrice(price);
            }
        }, 300);

        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
        };
    }, [deselectedEpisodesIds, episodes, totalCount, isBuyer, isSeller, isAdmin, program?.price]);

    const selectedEpisodesCount = totalCount - deselectedEpisodesIds.length;
    const selectedEpisodesDuration = useMemo(() => {
        if (deselectedEpisodesIds.length === 0) {
            return program?.duration ?? 0;
        }
        const deselectedDuration = episodes
            .filter((ep) => deselectedEpisodesIds.includes(ep.identifier.id))
            .reduce((sum, ep) => sum + (ep.duration ?? 0), 0);

        return (program?.duration ?? 0) - deselectedDuration;
    }, [episodes, deselectedEpisodesIds, program?.duration]);

    const [showAlreadyInCartModal, setShowAlreadyInCartModal] = useState<boolean>(false);
    const [addInCartErrorMessage, setAddInCartErrorMessage] = useState<string>('');
    const programUpdateQueueRef = useRef<Promise<void>>(Promise.resolve());

    const checkDetailsAvailability = (episode?: Program): boolean => {
        const details = episode ? episode.masterFile?.details : program?.masterFile?.details;
        if (!details) {
            return false;
        }

        const hasValidResolution =
            (details.resolution as ProgramDetailsProps)?.width != null &&
            (details.resolution as ProgramDetailsProps)?.height != null;

        const isIconActive =
            details.audio_mode != null ||
            details.aspect_ratio != null ||
            details.audio_bitrate != null ||
            details.video_bitrate != null ||
            details.audio_sample_rate != null ||
            hasValidResolution;

        return isIconActive;
    };

    const renderProgramDetailsIcon = () => {
        const isIconActive = checkDetailsAvailability();
        const details = program?.masterFile?.details;

        return isIconActive ? (
            <Image
                src={iconInfoWhite.src}
                alt="Plus d'informations"
                className="cursor-pointer mr-[16px]"
                width={48}
                height={48}
                onClick={() => {
                    if (details) {
                        setProgramDetails({
                            aspectRatio: details.aspect_ratio,
                            audioSampleRate: details.audio_sample_rate,
                            resolution: details.resolution,
                            audioBitrate: details.audio_bitrate,
                            videoBitrate: details.video_bitrate,
                            audioMode: details.audio_mode,
                        });
                    }
                }}
            />
        ) : (
            <></>
        );
    };

    const getProgramTitle = (program: Program, customLang?: string) => {
        const mainTitle = program.parent
            ? getFallbackTitle(program.parent.titles, customLang as LanguageIsoCode)
            : getFallbackTitle(program.titles, customLang as LanguageIsoCode);

        const mainLang = program.audioLanguages?.main ?? locale;

        const originalTitle =
            mainLang !== customLang
                ? program.parent
                    ? getFallbackTitle(program.parent.titles, mainLang, false)
                    : getFallbackTitle(program.titles, mainLang, false)
                : '';

        const title =
            originalTitle !== mainTitle ? `${mainTitle}${originalTitle ? ` (${originalTitle})` : ''}` : mainTitle;

        return title;
    };

    const getProgramYear = (program: Program) => {
        return program.firstBroadcastDate ? new Date(program.firstBroadcastDate).getUTCFullYear() : undefined;
    };

    const getProgramGenres = (program: Program) => {
        return program.genres
            .map((g: GenreProgram) => {
                return g.title;
            })
            .join(', ');
    };

    const getProgramAudioLanguages = (program: Program) => {
        if (!program.audioLanguages) return '';

        // Collect Program audio languages
        const allLanguages = program.audioLanguages.languages.map(
            (lang: LanguageIsoCode) => lang.toLowerCase() as LanguageIsoCode,
        );
        let languages = allLanguages;

        // Define main audio language
        const mainAudio = program.audioLanguages.main;

        // If logged as a Buyer
        if (isBuyer) {
            const buyerBroadcastLanguage = (userData as Buyer).broadcastLanguage;

            // Filter to keep only broadcast language and main audio language
            languages = languages.filter(
                (lang: LanguageIsoCode) =>
                    lang === mainAudio.toLowerCase() || lang === buyerBroadcastLanguage?.toLowerCase(),
            );

            // If none of broadcast language or main audio language are available, return all languages
            if (languages.length === 0) {
                languages = allLanguages;
            }
        }

        // Format and return languages
        return languages.map((lang: LanguageIsoCode) => `${tl(lang.toUpperCase() as LanguageIsoCode)}`).join(' / ');
    };

    const getProgramKeywords = (program: Program, customLocale: string) => {
        return (
            program.keywords?.[(customLocale || isoLocale) as LanguageIsoCode] ??
            program.keywords?.[(customLocale || isoEn) as LanguageIsoCode]
        );
    };

    const formatResolution = () => {
        const resolutionAvailable =
            (programDetails as ProgramDetailsProps)?.resolution?.width &&
            (programDetails as ProgramDetailsProps)?.resolution?.height;
        if (!resolutionAvailable) {
            return '';
        }
        return `${(programDetails as ProgramDetailsProps)?.resolution?.width} × ${(programDetails as ProgramDetailsProps)?.resolution?.height}`;
    };

    const formatAspectRatio = () => {
        const COMMON_RATIOS = [
            { label: '16:9', value: 16 / 9 },
            { label: '4:3', value: 4 / 3 },
            { label: '1:1', value: 1 },
            { label: '21:9', value: 21 / 9 },
            { label: '2.39:1', value: 2.39 },
        ];

        const TOLERANCE = 0.01;

        const rawRatio = (programDetails as ProgramDetailsProps)?.aspectRatio;
        const ratio = Number(rawRatio);

        if (!ratio || Number.isNaN(ratio)) {
            return '—';
        }

        const match = COMMON_RATIOS.find((r) => Math.abs(r.value - ratio) < TOLERANCE);

        // If standard ratio, only the label is displayed
        if (match) {
            return match.label;
        }

        // Otherwise, clean decimal display without ":1"
        return ratio.toFixed(2);
    };

    const formatBitrate = (value: number): string => {
        if (!Number.isFinite(value)) return '';

        return new Intl.NumberFormat(locale, {
            maximumFractionDigits: 0,
        })
            .format(value)
            .replace(/\u202F/g, ' ');
    };

    const formatAudioBitrate = () => {
        const raw = ((programDetails as ProgramDetailsProps)?.audioBitrate ??
            (programDetails as DetailsObject)?.audio_bitrate) as string | undefined;

        if (!raw) return '-';

        const cbrMatch = raw.match(/(\d+)\s*kbit\/s\s*\(CBR\)/i);
        if (cbrMatch) {
            const kbps = Number(cbrMatch[1]);

            if (kbps >= 1000) {
                return `${formatBitrate(kbps / 1000)} Mbit/s (CBR)`;
            }
            return `${formatBitrate(kbps)} kbit/s (CBR)`;
        }

        const vbrMatch = raw.match(/(\d+)\s*[–—-]\s*(\d+)\s*kbit\/s\s*\(VBR\)/i);
        if (vbrMatch) {
            const nominalKbps = Number(vbrMatch[1]);
            const maximumKbps = Number(vbrMatch[2]);
            const useMbit = nominalKbps >= 1000 || maximumKbps >= 1000;
            const divisor = useMbit ? 1000 : 1;
            const unit = useMbit ? 'Mbit/s' : 'kbit/s';
            const nominal = nominalKbps / divisor;
            const maximum = maximumKbps / divisor;
            return `${formatBitrate(nominal)}–${formatBitrate(maximum)} ${unit} (VBR)`;
        }

        return '-';
    };

    const objectToArray = (
        excludedCountries: Program['excludedCountries'],
    ): Array<{
        country: CountryIsoCode;
        type: RestrictionType[];
    }> => {
        if (!excludedCountries || !Array.isArray(excludedCountries)) return [];

        // Maps a backend enum value to the frontend restriction label
        const toRestriction = (exclusionType: ExclusionType): RestrictionType => {
            switch (exclusionType) {
                case ExclusionType.Fast:
                    return 'FAST';
                case ExclusionType.LocalVod:
                    return 'local_vod';
                default:
                    return 'classic';
            }
        };

        // Groups multiple backend entries for the same country into a single frontend entry
        const grouped = new Map<CountryIsoCode, RestrictionType[]>();

        for (const item of excludedCountries) {
            const restriction = toRestriction(item.exclusionType);
            const existing = grouped.get(item.countryISOCode);

            if (existing) {
                existing.push(restriction);
            } else {
                grouped.set(item.countryISOCode, [restriction]);
            }
        }

        return Array.from(grouped.entries()).map(([country, type]) => ({
            country,
            type,
        }));
    };

    // Maps a frontend restriction label back to the backend enum value
    const toExclusionType = (restriction: string): ExclusionType => {
        switch (restriction) {
            case 'FAST':
                return ExclusionType.Fast;
            case 'local_vod':
                return ExclusionType.LocalVod;
            default:
                return ExclusionType.Classic;
        }
    };

    const arrayToObject = (
        excludedCountriesArray: Array<{
            country: CountryIsoCode;
            type: string[];
        }>,
    ): Program['excludedCountries'] => {
        // Flattens: one frontend entry with N types becomes N backend entries (one per type)
        return excludedCountriesArray.flatMap((item) =>
            item.type.map((type) => ({
                countryISOCode: item.country,
                exclusionType: toExclusionType(type),
            })),
        );
    };

    const handleExcludedCountriesSave = async (
        ExcludedCountries: Array<{
            country: CountryIsoCode;
            type: string[];
        }>,
    ) => {
        if (!program) return;

        const restrictions = arrayToObject(ExcludedCountries);

        const updatedProgramContryRight = {
            ...program,
            excludedCountries: restrictions,
        };

        setProgram(updatedProgramContryRight);
        try {
            await enqueueProgramUpdate(updatedProgramContryRight);
        } catch {
            return;
        }
    };

    const updateArrayField = async (fieldName: string, newValues: string[]) => {
        if (!program) return;

        let currentValues: string[] = [];
        if (fieldName === 'genres') {
            currentValues = (program.genres ?? []).map((item: GenreProgram) => item.id);
        }

        const hasChanged = JSON.stringify([...newValues].sort()) !== JSON.stringify([...currentValues].sort());

        if (!hasChanged) return;

        let newObjects: Genre[] = [];
        if (fieldName === 'genres') {
            newObjects = genres.filter((genre) => newValues.includes(genre.id));
        }

        const updatedProgram = {
            ...program,
            [fieldName]: newObjects,
        };

        setProgram(updatedProgram);
        try {
            await enqueueProgramUpdate(updatedProgram);
        } catch {
            if (fieldName === 'genres') {
                setSelectedGenres(currentValues);
            }
        }
    };

    const updateEpisodeOrder = async (newOrder: Program[]): Promise<void> => {
        const updatedOrder = newOrder.map((episode: Program, index: number) => ({
            ...episode,
            episodeNumber: index + 1,
        }));

        for (const episode of updatedOrder) {
            await enqueueProgramUpdate(episode);
        }
    };

    const publishProgram = async (program: Program) => {
        const updatedProgram = {
            ...program,
            published: true,
        };
        setProgram(updatedProgram);
        try {
            await enqueueProgramUpdate(updatedProgram);
        } catch {
            return;
        }
    };

    const unpublishProgram = async (program: Program) => {
        const updatedProgram = {
            ...program,
            published: false,
        };
        setProgram(updatedProgram);
        try {
            await enqueueProgramUpdate(updatedProgram);
        } catch {
            return;
        }
    };

    const validateProgram = async (program: Program) => {
        const updatedProgram = {
            ...program,
            validated: true,
            published: true,
        };
        setProgram(updatedProgram);
        try {
            await enqueueProgramUpdate(updatedProgram);
        } catch {
            return;
        }
    };

    function getEnglishSubtitleIdFromProgram(program: Program, source: 'trailer' | 'master'): string {
        const subtitles = source === 'trailer' ? program.trailerSubtitles : program.masterSubtitles;
        return subtitles?.find((sub) => sub.language.toUpperCase() === 'EN')?.sourceFile?.id || '';
    }

    const [showEpisodeMaster, setShowEpisodeMaster] = useState<boolean>(false);
    const [episodeMaster, setEpisodeMaster] = useState<string>('');
    const [episodeMasterSubTitles, setEpisodeMasterSubTitles] = useState<string>('');
    function playEpisodeMaster(episode: Program) {
        setShowEpisodeMaster(true);

        const master = episode.masterFile?.tenant
            ? `${episode.masterFile.tenant.cloudFrontUrl}/masters/${episode.masterFile.id}/adaptive/output.m3u8`
            : '';
        setEpisodeMaster(master);
        const subTitles =
            getEnglishSubtitleIdFromProgram(episode, 'master') !== ''
                ? `${episode.masterFile?.tenant?.cloudFrontUrl}/subtitles/${getEnglishSubtitleIdFromProgram(episode, 'master')}/source.srt`
                : '';
        setEpisodeMasterSubTitles(subTitles);
    }

    const router = useRouter();
    const [isEditView, setIsEditView] = useState<boolean>(false);
    const canSelectEpisodes = isBuyer && !isReadOnly && !isEditView;
    const shouldShowSelectedEpisodesLength = canSelectEpisodes && deselectedEpisodesIds.length > 0;
    useEffect(() => {
        const fetchProgramById = async () => {
            setLoading(true);
            try {
                if (!id) {
                    setProgram(null);
                    return;
                }

                let includeSensitiveData = (isAdmin || isSeller) && !isReadOnly;
                let includeMasterFiles = isAdmin || isSeller || isBuyer;
                let skipAuth = false;

                let programById: Program | null;
                try {
                    programById = await getProgram({ id, includeSensitiveData, includeMasterFiles });
                } catch {
                    programById = null;
                    if (isSeller) {
                        try {
                            includeSensitiveData = false;
                            includeMasterFiles = false;
                            skipAuth = true;
                            programById = await getProgram({
                                id,
                                includeSensitiveData,
                                includeMasterFiles,
                                skipAuth,
                            });
                        } catch {
                            programById = null;
                        }
                    }
                }

                setIsEditView(includeSensitiveData);

                if (!programById) {
                    setProgram(null);
                    return;
                }

                if (programById.parent) {
                    router.replace({
                        pathname: '/programs/[id]',
                        params: { id: programById.parent.identifier.id },
                    });
                }

                setSelectedGenres(programById.genres.map((g: GenreProgram) => g.id));

                if (isSeller || isAdmin) {
                    const categoriesRes = await fetchCategoriesList({ contentType: ContentType.Program });
                    setCategories(categoriesRes.data);
                    const genresRes = await await fetchGenresList({ contentType: ContentType.Program });
                    setGenres(genresRes.data);
                }

                setProgram(programById);
            } catch (error) {
                const logError = console.error;
                logError(error);
                setProgram(null);
            } finally {
                setLoading(false);
            }
        };

        if (!loadingUser && id) {
            fetchProgramById();
        }
    }, [id, isAdmin, isSeller, isBuyer, isReadOnly, loadingUser, router]);

    useEffect(() => {
        const handleScroll = () => {
            const scrollElement = document.documentElement;
            const { scrollTop, clientHeight, scrollHeight } = scrollElement;
            const offset = 300;
            const nearBottom = scrollTop + clientHeight >= scrollHeight - offset;

            if (nearBottom && hasMore && !loadingMore) {
                loadMoreEpisodes();
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [hasMore, loadingMore, loadMoreEpisodes]);

    type ProgramStringField =
        | 'titles'
        | 'synopsis'
        | 'keywords'
        | 'directors'
        | 'casting'
        | 'awards'
        | 'imdb'
        | 'firstBroadcastDate'
        | 'productionCountryIsoCode'
        | 'category';
    type MultiLangField =
        | 'titles'
        | 'synopsis'
        | 'keywords'
        | 'directors'
        | 'casting'
        | 'awards'
        | 'imdb'
        | 'firstBroadcastDate'
        | 'productionCountryIsoCode'
        | 'category';

    const updateCurrentProgram = async (program: Program) => {
        const excludedCountriesInput = program.excludedCountries?.map(({ countryISOCode, exclusionType }) => ({
            countryISOCode,
            exclusionType,
        }));
        await updateProgram({
            id: program.identifier.id,
            awards: program.awards,
            casting: program.casting,
            categoryId: program.category?.id,
            directors: program.directors,
            episodeNumber: Number(program.episodeNumber),
            firstBroadcastDate: program.firstBroadcastDate ? formatISO(program.firstBroadcastDate as string) : null,
            genreIds: program.genres.map((g) => g.id),
            imdb: program.imdb ? program.imdb : null,
            keywords: program.keywords,
            productionCountry: program.productionCountryIsoCode
                ? (program.productionCountryIsoCode.toUpperCase() as CountryIsoCode)
                : null,
            published: program.published,
            synopsis: program.synopsis,
            titles: program.titles,
            validated: program.validated,
            isSegmentable: program.isSegmentable,
            isExclusive: program.isExclusive,
            excludedCountries: excludedCountriesInput || [],
        });
    };

    // Serialize autosave mutations to avoid overlapping updateProgram requests.
    const enqueueProgramUpdate = (programToUpdate: Program) => {
        const queuedUpdate = programUpdateQueueRef.current.then(
            () => updateCurrentProgram(programToUpdate),
            () => updateCurrentProgram(programToUpdate),
        );

        programUpdateQueueRef.current = queuedUpdate.then(
            () => undefined,
            () => undefined,
        );

        return queuedUpdate;
    };

    const handleToggleChange = async ({
        isSegmentable,
        isExclusive,
    }: {
        isSegmentable: boolean;
        isExclusive: boolean;
    }) => {
        if (!program) return;

        const updatedProgram = {
            ...program,
            isSegmentable,
            isExclusive,
        };

        setProgram(updatedProgram);
        try {
            await enqueueProgramUpdate(updatedProgram);
        } catch {
            return;
        }
    };

    const updateMultiLanguageField = async (
        fieldName: MultiLangField,
        newValue: string,
        localeToChange = '',
        programToChange: Program | null = null,
        isEpisode?: boolean,
    ) => {
        if (!program) return;
        const _locale = (localeToChange || locale) as LanguageIsoCode;
        const _program = programToChange ?? program;

        const map = (_program[fieldName] as Partial<Record<LanguageIsoCode, string>>) ?? {};
        const currentValue = map[_locale] ?? map[isoEn as LanguageIsoCode] ?? '';
        if (newValue === currentValue) return;

        const updatedProgram: Program = {
            ..._program,
            [fieldName]: { ...map, [_locale]: newValue } as Partial<Record<LanguageIsoCode, string>>,
        };

        if (!isEpisode) setProgram(updatedProgram);
        try {
            await enqueueProgramUpdate(updatedProgram);
        } catch {
            return;
        }
    };

    const updateStringField = async (fieldName: ProgramStringField, newValue: string | null) => {
        let updatedValue = newValue;
        if (!program) return;

        if (fieldName === 'category') {
            const newCategory = categories.find((cat) => cat.id === updatedValue);
            if (!newCategory) return;
            if (program.category?.id === updatedValue) return;

            const categoryProgram: CategoryProgram = {
                id: newCategory.id,
                title: '',
                catchPhrase: '',
                image: newCategory.image,
            };
            categoryProgram.title = newCategory.title;

            const updatedProgram = {
                ...program,
                category: categoryProgram,
            };

            setProgram(updatedProgram);
            try {
                await enqueueProgramUpdate(updatedProgram);
            } catch {
                return;
            }
            return;
        }

        const currentValue = program[fieldName] ?? '';

        if (fieldName === 'productionCountryIsoCode' && newValue) {
            if (newValue.toLowerCase() === (currentValue as string).toLowerCase()) updatedValue = null;
        }

        if (updatedValue === currentValue) return;

        const updatedProgram = {
            ...program,
            [fieldName]: updatedValue,
        };

        setProgram(updatedProgram);
        try {
            await enqueueProgramUpdate(updatedProgram);
        } catch {
            return;
        }
    };

    const getMultiLanguageValue = (program: Program, fieldName: MultiLangField, customLocale?: string) => {
        const map = program[fieldName] as Partial<Record<LanguageIsoCode, string>> | undefined;
        const loc = (customLocale ? customLocale : isoLocale) as LanguageIsoCode;
        return map?.[loc] ?? map?.[isoEn as LanguageIsoCode] ?? '';
    };

    const updateEpisodeTitle = async (
        newValue: string,
        locale: LanguageIsoCode,
        currentEpisode: Program,
        episodes: Program[],
    ) => {
        const updatedEpisode: Program = {
            ...currentEpisode,
            titles: {
                ...currentEpisode.titles,
                [locale]: newValue,
            },
        };

        try {
            await enqueueProgramUpdate(updatedEpisode);
        } catch {
            return;
        }

        const newEpisodes = episodes.map((episode) => {
            if (episode.identifier.id === updatedEpisode.identifier.id) {
                return updatedEpisode;
            }

            return episode;
        });

        setEpisodes(newEpisodes);
    };

    const h1Ref = useRef<HTMLHeadingElement>(null);
    const buttonRef = useRef<HTMLDivElement>(null);

    const [currentLocaleFieldView, setCurrentLocaleFieldView] = useState<LanguageIsoCode>(isoLocale);

    const mainContainerClassName: string = episodes.length > 0 ? 'inline-block' : 'flex-row lg:flex-col';
    const col1ClassName: string = 'inline-block w-full lg:w-4/6 lg:pr-15';
    const col2ClassName: string = 'w-full lg:w-2/6 inline-block lg:float-right my-8 lg:my-0';
    const col3ClassName: string = 'w-full lg:w-4/6 lg:my-15 lg:pr-15 flex-col inline-block';

    const [currentSubTitleUrl, setCurrentSubTitleUrl] = useState<string>('');
    const [currentVideoUrl, setCurrentVideoUrl] = useState<string>('');

    const locale = useLocale().toUpperCase() as LanguageIsoCode;

    function setCurrentVideoData(program: Program, source: 'trailers' | 'masters') {
        const file = source === 'trailers' ? program.trailerFile : program.masterFile;
        const isValidFile = file?.tenant?.cloudFrontUrl && file.id;
        const videoUrl =
            isValidFile && file.tenant ? `${file.tenant.cloudFrontUrl}/${source}/${file.id}/adaptive/output.m3u8` : '';

        setCurrentVideoUrl(videoUrl);

        const subtitles = source === 'trailers' ? program.trailerSubtitles : program.masterSubtitles;
        const englishSubtitle = subtitles?.find((sub) => sub.language?.toUpperCase() === 'EN');
        const subtitleId = englishSubtitle?.sourceFile?.id;
        const subtitleUrl =
            subtitleId && file?.tenant?.cloudFrontUrl
                ? `${file.tenant.cloudFrontUrl}/subtitles/${subtitleId}/source.srt`
                : '';
        setCurrentSubTitleUrl(subtitleUrl);
    }

    if (loading)
        return (
            <div className="w-full flex justify-center">
                <ProgramLoadingPage />
            </div>
        );
    if (!program) {
        notFound();
    }

    function formatProgramPrice(locale: string, value: number) {
        const price = Number.isNaN(value) ? 0 : value;
        return new Intl.NumberFormat(localeMap[locale], {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0,
        }).format(Math.floor(Number(price)));
    }
    return (
        <div className="py-[50px] w-[89%] mx-auto flex flex-col lg:flex-row lg:items-start">
            <div className="w-full">
                <div className={mainContainerClassName}>
                    <div className={col1ClassName}>
                        <div className="flex flex-col items-start md:flex-row gap-7 lg:gap-0">
                            <div className="max-w-[100vw] md:max-w-[241px] w-full">
                                <div className="aspect-3/4 relative">
                                    <div className="mb-4">
                                        <CoverImage program={program} />
                                    </div>
                                    {isAnonymous ? (
                                        <Image
                                            src={iconPlayWhite.src}
                                            alt="icon play"
                                            width={48}
                                            height={48}
                                            className="drop-shadow-[0_0_10px_rgba(0,0,0,0.30)] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                                            onClick={() => setIsNotRegistered(true)}
                                        />
                                    ) : program.trailerFile?.tenant ? (
                                        <Image
                                            src={iconPlayWhite.src}
                                            alt="icon play"
                                            width={48}
                                            height={48}
                                            className="drop-shadow-[0_0_10px_rgba(0,0,0,0.30)] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                                            onClick={() => {
                                                setCurrentVideoData(program, 'trailers');
                                                setShowVideoModal(true);
                                            }}
                                        />
                                    ) : undefined}
                                </div>
                                {isEditView ? (
                                    <div>
                                        {!program.validated ? (
                                            <>
                                                <p className="text-sm text-center py-[20px] leading-[20px]">
                                                    {t('sellerView.info')}
                                                </p>
                                                <Button
                                                    className="w-full bg-[#1A7CAD] text-[#ffffff] font-normal rounded-[4px] cursor-pointer"
                                                    onClick={() => validateProgram(program)}
                                                >
                                                    <IconValidate className="size-4" />
                                                    {t('sellerView.validateLabel')}
                                                </Button>
                                                <p className="text-sm text-center py-[20px] leading-[20px]">
                                                    {t('sellerView.agreeTerms')}{' '}
                                                    <a
                                                        className="underline underline-offset-1"
                                                        href="https://doc.bunqer.tech/Terms-conditions-25504e781dc280189385dd68fe1001ce"
                                                    >
                                                        {t('sellerView.readTerms')}
                                                    </a>
                                                </p>
                                            </>
                                        ) : !program.published ? (
                                            <>
                                                <p className="text-sm text-center py-[20px] leading-[20px]">
                                                    {t('sellerView.draft')}
                                                </p>
                                                <Button
                                                    className="w-full bg-[#29B82E] text-[#ffffff] font-normal rounded-[4px] cursor-pointer"
                                                    onClick={() => setIsPublishProgramModalOpen(true)}
                                                >
                                                    <IconPublish className="size-4" />
                                                    {t('sellerView.publishLabel')}
                                                </Button>
                                            </>
                                        ) : (
                                            <>
                                                <p className="text-sm text-center py-[20px] leading-[20px]">
                                                    {t('sellerView.published')}
                                                </p>
                                                <Button
                                                    className="w-full bg-[#D91E1E] text-[#ffffff] font-normal rounded-[4px] cursor-pointer"
                                                    onClick={() => unpublishProgram(program)}
                                                >
                                                    <IconUnpublish className="size-4" />
                                                    {t('sellerView.unpublished')}
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                ) : null}
                            </div>
                            <div className="md:ml-15 flex-1">
                                <div className="flex text-left relative flex-col border-b-2 min-h-[320px]">
                                    <h1 ref={h1Ref} className="flex items-center text-2xl font-bold mb-2">
                                        {isEditView ? (
                                            <>
                                                {Object.entries(program.titles || {}).map(([lang]) => (
                                                    <AutoResizeInput
                                                        key={lang}
                                                        parentRef={h1Ref}
                                                        value={getMultiLanguageValue(
                                                            program,
                                                            'titles',
                                                            currentLocaleFieldView,
                                                        )}
                                                        currentLocale={currentLocaleFieldView}
                                                        className={`mr-1 text-[24px] flex-1 px-2 ${formInputClassName} ${currentLocaleFieldView !== lang ? 'hidden' : ''}`}
                                                        onChange={(e) => {
                                                            const currentValue = getMultiLanguageValue(
                                                                program,
                                                                'titles',
                                                                currentLocaleFieldView,
                                                            );
                                                            let newValue = e.target.value;
                                                            if (newValue === '') {
                                                                newValue = currentValue;
                                                            }
                                                            e.target.value = newValue;
                                                        }}
                                                        onBlur={(e) => {
                                                            const currentValue = getMultiLanguageValue(
                                                                program,
                                                                'titles',
                                                                currentLocaleFieldView,
                                                            );
                                                            const trimmed = e.target.value.trim();
                                                            if (!trimmed) {
                                                                e.target.value = currentValue;
                                                                updateMultiLanguageField(
                                                                    'titles',
                                                                    currentValue,
                                                                    currentLocaleFieldView,
                                                                );
                                                            } else {
                                                                updateMultiLanguageField(
                                                                    'titles',
                                                                    trimmed,
                                                                    currentLocaleFieldView,
                                                                );
                                                            }
                                                        }}
                                                    />
                                                ))}
                                                {getProgramYear(program) ? (
                                                    <span className="flex-1 min-w-[80px]">
                                                        - {getProgramYear(program)}
                                                    </span>
                                                ) : (
                                                    ''
                                                )}
                                            </>
                                        ) : (
                                            `${getProgramTitle(program, currentLocaleFieldView)} ${getProgramYear(program) ? `- ${getProgramYear(program)}` : ''}`
                                        )}
                                    </h1>
                                    <div className="flex items-stretch leading-none mb-[19px]">
                                        <div className="text-[14px]">
                                            {tp('synopsisLabel')}{' '}
                                            {Object.entries(program.synopsis || {}).map(([lang], index, array) => (
                                                <span key={lang}>
                                                    <span
                                                        className={`cursor-pointer ${lang === currentLocaleFieldView ? 'underline' : ''}`}
                                                        onClickCapture={() => {
                                                            setCurrentLocaleFieldView(lang as LanguageIsoCode);
                                                        }}
                                                    >
                                                        {lang.toUpperCase()}
                                                    </span>
                                                    {index < array.length - 1 || !isEditView ? ' / ' : ''}
                                                </span>
                                            ))}
                                            {!isEditView ? (
                                                <button
                                                    className="cursor-pointer text-[14px]"
                                                    type="button"
                                                    onClick={() => setShowOtherLanguage(true)}
                                                >
                                                    {tp('otherLanguageLabel')}
                                                </button>
                                            ) : undefined}
                                        </div>
                                    </div>
                                    <div className="pb-5">
                                        {isEditView ? (
                                            <div>
                                                {Object.entries(program.synopsis || {}).map(([lang]) => (
                                                    <textarea
                                                        key={lang}
                                                        id={lang}
                                                        defaultValue={program.synopsis?.[lang as LanguageIsoCode] ?? ''}
                                                        rows={4}
                                                        className={`mb-3 text-[16px] w-full ${formInputClassName} font-medium ${currentLocaleFieldView !== lang ? 'hidden' : ''}`}
                                                        onBlur={(e) => {
                                                            const newValue = e.target.value.trim();

                                                            const currentValue =
                                                                program.synopsis?.[lang as LanguageIsoCode] ??
                                                                program.synopsis?.[isoEn] ??
                                                                '';

                                                            if (newValue === '') {
                                                                e.target.value = currentValue;
                                                                return;
                                                            }

                                                            updateMultiLanguageField('synopsis', newValue, lang);
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                        ) : (
                                            (program.synopsis?.[currentLocaleFieldView as LanguageIsoCode] ?? '')
                                        )}
                                    </div>
                                    <div className="mt-auto w-full flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 lg:gap-0 flex-wrap">
                                        {shouldShowSelectedEpisodesLength ? (
                                            <p className="pb-4 mr-2 font-bold">
                                                {tp('selectedEpisodesLength', {
                                                    episodesQty: `${selectedEpisodesCount}`,
                                                    episodesDuration: formatDuration(selectedEpisodesDuration),
                                                })}
                                            </p>
                                        ) : (
                                            <p className="pb-4 mr-2 font-bold">
                                                {tp('programLength', {
                                                    duration: program.duration ? formatDuration(program.duration) : '0',
                                                })}
                                            </p>
                                        )}
                                        <div className="flex items-center pb-4">
                                            {episodes.length === 0 && renderProgramDetailsIcon()}
                                            {isAnonymous ? (
                                                <Image
                                                    src={iconPlayBorderWhite.src}
                                                    alt={tp('buttonVideoLabel')}
                                                    className="cursor-pointer mr-[20px]"
                                                    width={48}
                                                    height={48}
                                                    onClick={() => setIsNotRegistered(true)}
                                                />
                                            ) : program.masterFile?.tenant ? (
                                                <Image
                                                    src={iconPlayBorderWhite.src}
                                                    alt={tp('buttonVideoLabel')}
                                                    className="cursor-pointer mr-[20px]"
                                                    width={48}
                                                    height={48}
                                                    onClick={() => {
                                                        setCurrentVideoData(program, 'masters');
                                                        setShowVideoModal(true);
                                                    }}
                                                />
                                            ) : undefined}

                                            {!isEditView && !isReadOnly && !isSeller ? (
                                                <div ref={buttonRef} className="md:flex-1">
                                                    <AppButton
                                                        innerText={
                                                            calculatedPrice !== null && episodes.length > 0
                                                                ? tp('buttonLabel', {
                                                                      price: formatProgramPrice(
                                                                          locale.toLowerCase(),
                                                                          calculatedPrice,
                                                                      ),
                                                                  })
                                                                : program.price
                                                                  ? tp('buttonLabel', {
                                                                        price: formatProgramPrice(
                                                                            locale.toLowerCase(),
                                                                            program.price,
                                                                        ),
                                                                    })
                                                                  : tp('buttonLabelNoPrice')
                                                        }
                                                        btnBgColor="#DD06AE"
                                                        customClassName="pb-4 text-[14px] font-medium py-[15px]"
                                                        onClick={() => {
                                                            if (isBuyer) {
                                                                setShowModalCart(true);
                                                            } else {
                                                                setIsNotRegistered(true);
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            ) : undefined}
                                        </div>
                                    </div>
                                </div>
                                {isEditView ? (
                                    <div className="w-full max-w-[240px] text-center mx-auto mt-[10px]">
                                        <div className="text-[14px] my-[20px] px-[1.5rem]">
                                            {t('territoriesRight.label')}
                                        </div>
                                        <Button
                                            className="w-full cursor-pointer"
                                            onClick={() => {
                                                setIsExcludedCountriesModalOpen(true);
                                            }}
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                className="size-[24px]"
                                            >
                                                <title>territories</title>
                                                <path
                                                    d="M9.70825 21.611C10.1254 21.6857 10.5842 21.7263 11.1101 21.7351H11.1229C11.5312 21.7351 11.8656 21.4077 11.8726 20.9976C11.8795 20.5837 11.5492 20.2421 11.135 20.2351C10.7912 20.2295 10.4998 20.2038 10.2191 20.1662C9.17573 19.0175 8.3692 17.7035 7.80775 16.2075H10.9124C11.3265 16.2075 11.6624 15.8717 11.6624 15.4575C11.6624 15.0433 11.3265 14.7075 10.9124 14.7075H7.36304C7.16368 13.8238 7.05615 12.9184 7.05615 12.0001C7.05615 11.0884 7.16565 10.1827 7.36853 9.29272H15.6526C16.0668 9.29272 16.4026 8.95691 16.4026 8.54272C16.3613 8.13596 16.1993 7.73894 16.0613 7.35913C15.8906 6.8446 15.6936 6.36963 15.4607 5.9115C15.3091 5.60901 15.1498 5.31421 14.9744 5.02527C14.8747 4.85274 14.767 4.68236 14.6576 4.51248C15.812 5.04097 16.839 5.83519 17.6521 6.84827C18.1384 7.4613 18.5317 8.12781 18.8218 8.82947C18.9803 9.21216 19.4194 9.39197 19.8018 9.23596C20.1844 9.07739 20.3665 8.63867 20.2083 8.25598C19.8647 7.42578 19.4 6.63879 18.8247 5.91296C17.2994 4.01233 15.1432 2.75989 12.761 2.38782C12.2662 2.30542 11.7744 2.26514 11.2126 2.26514C10.6956 2.26514 10.2037 2.30542 9.71667 2.38672C7.32165 2.76062 5.16248 4.01306 3.63464 5.91663C2.25888 7.65691 1.49954 9.78514 1.5 12.0001C1.5 13.2952 1.75328 14.5523 2.24977 15.7312C2.25439 15.7428 2.25874 15.7536 2.26392 15.7648C2.60614 16.5872 3.06679 17.3673 3.63757 18.0873C5.16321 19.9875 7.32495 21.24 9.70825 21.611ZM7.80171 19.4837C6.64696 18.9552 5.6212 18.1623 4.81018 17.152C4.57054 16.8497 4.35452 16.534 4.16098 16.2075H6.2178C6.2558 16.3411 6.31714 16.5468 6.36768 16.6503C6.73242 17.657 7.22026 18.5991 7.80171 19.4837ZM10.2112 3.83545C10.8522 3.75113 11.5502 3.74431 12.252 3.83549C12.8023 4.45526 13.2881 5.10478 13.6835 5.78882C13.8453 6.05615 13.9856 6.31653 14.1215 6.58752C14.3158 6.96935 14.4785 7.36595 14.6221 7.79272H7.81219C8.35409 6.31689 9.16374 4.99007 10.2112 3.83545ZM4.80725 6.85193C5.61923 5.84041 6.64352 5.04692 7.79732 4.51775C7.22667 5.39056 6.74625 6.32193 6.38379 7.31921C6.31233 7.4586 6.2693 7.63397 6.22362 7.79272H4.16075C4.35361 7.4673 4.56899 7.15245 4.80725 6.85193ZM3 12.0001C3 11.0683 3.16434 10.1625 3.47159 9.29272H5.83823C5.65796 10.1848 5.55615 11.0892 5.55615 12.0001C5.55615 12.9162 5.65636 13.8202 5.83369 14.7075H3.47122C3.16466 13.84 3 12.9333 3 12.0001Z"
                                                    fill="white"
                                                />
                                                <path
                                                    d="M20.1075 13.2751H20.0475V12.165C20.0475 10.7326 18.885 9.57007 17.4525 9.57007C16.02 9.57007 14.8575 10.7326 14.8575 12.165V13.2751H14.79C13.4775 13.2751 12.405 14.3476 12.405 15.6676V19.3425C12.405 20.6626 13.4775 21.735 14.79 21.735H20.1075C21.4275 21.735 22.5 20.6626 22.5 19.3425V15.6676C22.5 14.3476 21.4275 13.2751 20.1075 13.2751ZM18.2025 18.3376C18.2025 18.7501 17.865 19.0876 17.4525 19.0876C17.04 19.0876 16.7025 18.7501 16.7025 18.3376V16.6725C16.7025 16.2601 17.04 15.9225 17.4525 15.9225C17.865 15.9225 18.2025 16.2601 18.2025 16.6725V18.3376ZM18.5475 13.2751H16.3575V12.165C16.3575 11.565 16.845 11.0701 17.4525 11.0701C18.0525 11.0701 18.5475 11.565 18.5475 12.165V13.2751Z"
                                                    fill="white"
                                                />
                                            </svg>
                                            {t('territoriesRight.buttonLabel')}
                                        </Button>
                                    </div>
                                ) : undefined}
                            </div>
                        </div>
                        {showModalCart && (
                            <ModalCartPage
                                program={selectedEpisode || program}
                                excludeEpisodeIds={selectedEpisode ? undefined : deselectedEpisodesIds}
                                onClose={() => {
                                    setShowModalCart(false);
                                    setSelectedEpisode(null);
                                }}
                                onError={(message) => {
                                    setAddInCartErrorMessage(message);
                                    setShowAlreadyInCartModal(true);
                                }}
                            />
                        )}
                    </div>
                    <Metadata
                        program={program}
                        user={userData}
                        isAdmin={isAdmin}
                        isTechnicalInfoActive={isTechnicalInfoCardActive}
                        isBuyer={isBuyer}
                        isSeller={isEditView}
                        isInfoOpen={isInfoOpen}
                        handleToggleChange={handleToggleChange}
                        setIsInfoOpen={setIsInfoOpen}
                        categories={categories.sort((a, b) => a.title.localeCompare(b.title))}
                        genres={genres.sort((a, b) => a.title.localeCompare(b.title))}
                        selectedGenres={selectedGenres}
                        setSelectedGenres={setSelectedGenres}
                        countries={countries}
                        formInputClassName={`${formInputClassName} font-normal`}
                        t={t as unknown as (key: string, params?: Record<string, string | number>) => string}
                        tc={tc}
                        updateStringField={updateStringField}
                        updateArrayField={updateArrayField}
                        updateMultiLanguageField={updateMultiLanguageField}
                        getMultiLanguageValue={getMultiLanguageValue}
                        getProgramYear={getProgramYear}
                        getProgramGenres={getProgramGenres}
                        getProgramKeywords={getProgramKeywords}
                        getProgramAudioLanguages={getProgramAudioLanguages}
                        currentLocaleFieldView={currentLocaleFieldView}
                        containerClassName={col2ClassName}
                    />
                    {episodes && (
                        <EpisodesList
                            containerClassName={col3ClassName}
                            isAnon={!isSeller && !isAdmin && !isBuyer}
                            isSeller={isSeller}
                            openInfoModal={(episodeDetails: DetailsObject) => setProgramDetails(episodeDetails)}
                            mainProgramTitle={getProgramTitle(program, currentLocaleFieldView)}
                            episodes={episodes}
                            loadingInitial={loadingInitial}
                            loadingMore={loadingMore}
                            totalCount={totalCount}
                            isEditView={isEditView}
                            setTechnicalInfoCardActive={setIsTechnicalInfoCardActive}
                            checkDetailsAvailability={(episode: Program) => checkDetailsAvailability(episode)}
                            showPurchaseButton={false}
                            isReadOnly={isReadOnly}
                            user={user ? user : undefined}
                            locale={currentLocaleFieldView}
                            formInputClassName={`${formInputClassName}`}
                            editViewEpisodeClassName={editViewEpisodeClassName}
                            t={tp as unknown as (key: string, params?: Record<string, string | number>) => string}
                            formateNumber={formateNumber}
                            getMultiLanguageValue={getMultiLanguageValue}
                            setSelectedEpisode={setSelectedEpisode}
                            setShowModalCart={setShowModalCart}
                            setIsNotRegistered={setIsNotRegistered}
                            setEpisodes={setEpisodes}
                            updateEpisodeOrder={updateEpisodeOrder}
                            playMaster={playEpisodeMaster}
                            updateEpisodeTitle={updateEpisodeTitle}
                            buttonRef={buttonRef}
                            deselectedEpisodesIds={deselectedEpisodesIds}
                            onSelectedEpisodesChange={setDeselectedEpisodesIds}
                        />
                    )}
                </div>
            </div>
            {showVideoModal && currentVideoUrl && user && (
                <VideoModal
                    showModal={showVideoModal}
                    onClose={() => setShowVideoModal(false)}
                    src={currentVideoUrl}
                    subtitles={currentSubTitleUrl}
                />
            )}
            {showEpisodeMaster ? (
                <VideoModal
                    showModal={showEpisodeMaster}
                    onClose={() => setShowEpisodeMaster(false)}
                    src={episodeMaster}
                    subtitles={episodeMasterSubTitles}
                />
            ) : undefined}
            {programDetails && (
                <TechnicalInfoModal
                    isOpen={!!programDetails}
                    onClose={() => setProgramDetails(undefined)}
                    resolution={formatResolution()}
                    aspectRatio={formatAspectRatio()}
                    videoBitrate={(programDetails as ProgramDetailsProps)?.videoBitrate / 1000000}
                    audioBitrate={formatAudioBitrate()}
                    audioSampleRate={(programDetails as ProgramDetailsProps)?.audioSampleRate / 1000}
                    audioMode={programDetails?.audioMode as string}
                />
            )}
            {isNotRegistered && (
                <Modal
                    isOpen={true}
                    onClose={() => setIsNotRegistered(false)}
                    ModalSize="max-w-[425px] w-full"
                    closePosition="right-[16px] top-[20px]"
                    modalPadding="pb-[74px] pt-[64px]"
                    rounded="rounded-[8px]"
                >
                    <div className="flex flex-col gap-5 items-center">
                        <p className="text-center font-normal mt-3">{t('modal.reserved.text')}</p>
                        <AppButton
                            customClassName="text-[14px] font-medium py-[15px]"
                            innerText={t('modal.reserved.buttonLabel')}
                            btnBgColor="#DD06AE"
                            externalUrl="mailto:contact@bunqer.tech"
                        />
                    </div>
                </Modal>
            )}
            <ConfirmPublicationModal
                isOpen={isPublishProgramModalOpen}
                onClose={() => setIsPublishProgramModalOpen(false)}
                onConfirm={() => publishProgram(program)}
            />

            {showOtherLanguage && (
                <OtherLanguageModal isOpen={showOtherLanguage} onClose={() => setShowOtherLanguage(false)} />
            )}
            {isEditView && (
                <ExcludedCountriesModals
                    isOpen={isExcludedCountriesModalOpen}
                    onClose={() => {
                        document.body.style.overflow = '';
                        setIsExcludedCountriesModalOpen(false);
                    }}
                    initialExcludedCountries={objectToArray(program?.excludedCountries)}
                    onSave={handleExcludedCountriesSave}
                />
            )}
            {showAlreadyInCartModal ? (
                <Modal
                    ModalSize="max-w-[425px] w-full"
                    closePosition="right-[16px] top-[20px]"
                    modalPadding="pb-[74px] pt-[64px]"
                    rounded="rounded-[8px]"
                    isOpen={true}
                    onClose={() => setShowAlreadyInCartModal(false)}
                >
                    <p className="text-center">{getTranslatedCartErrorMessage(addInCartErrorMessage)}</p>
                </Modal>
            ) : undefined}
        </div>
    );
}
